import { SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import TableCell, { CellProps, VerticalAlignment } from '~/components/Table/TableCell';
import TableRow, { MessageRow } from '~/components/Table/TableRow';
import { Property } from 'csstype';
import kebabCase from 'lodash-es/kebabCase';
import * as React from 'react';

type CustomStyleFunctionProps<T> = {
  data: T;
  rowIndex: number;
};
export interface Column<T> {
  header: Maybe<string | React.ReactNode>;
  cell: React.ComponentType<CellProps & { data: T; rowIndex?: number }>;
  onCellClick?: (rowIndex: number) => void;
  width?: string;
  minWidth?: string;
  cursor?: Property.Cursor;
  noLeftPadding?: boolean;
  noRightPadding?: boolean;
  noPadding?: boolean;
  verticalAlign?: VerticalAlignment;
  // Custom styling can be provided for the cell and header
  customCellStyles?: SerializedStyles | ((props: CustomStyleFunctionProps<T>) => SerializedStyles);
  customHeaderStyles?: SerializedStyles | (() => SerializedStyles);
}

// https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_Grid_Layout
type GridConfig = {
  grid?: string;
  gridTemplateColumns?: string;
  gridTemplateRows?: string;
  gridTemplateAreas?: string;
  gridTemplate?: string;
  gridAutoColumns?: string;
  gridAutoRows?: string;
  gridAutoFlow?: string;
  gridRowStart?: string;
  gridRowEnd?: string;
  gridColumnEnd?: string;
  gridRow?: string;
  gridColumn?: string;
  gridArea?: string;
  rowGap?: string;
  columnGap?: string;
  gap?: string;
};

interface TableProps<T> {
  columns: Column<T>[];
  fixedFirstRow?: Maybe<Column<T>[]>;
  data: T[];
  onRowClick?: (rowIndex: number) => void;
  rowClickTag?: string;
  selectedIndexes?: number[];
  className?: string;
  columnVerticalAlignment?: VerticalAlignment;
  customRowStyles?: SerializedStyles | ((props: CustomStyleFunctionProps<T>) => SerializedStyles);
  // This props only use is the "You have new orders" message.
  // We could change that and remove this functionality.
  message?: Maybe<string>;
  messageTag?: string;
  onMessageClick?: () => void;
  // Providing this config specifies table to be rendered with display: grid;
  grid?: GridConfig;
  autoLayout?: boolean;
  children?: React.ReactNode;
}

interface ContainerProps {
  grid?: GridConfig;
  autoLayout?: boolean;
}

const Container = styled('table')<ContainerProps>`
  width: 100%;
  border-collapse: collapse;
  table-layout: ${({ autoLayout }) => (autoLayout ? 'auto' : 'fixed')};
  ${({ grid }) =>
    grid
      ? Object.entries(grid).reduce(
          (css, [key, val]) => (val ? `${css}${kebabCase(key)}:${val};` : css),
          'display:grid;',
        )
      : ''}
`;

const TableHeader = styled('thead')<{ asGrid?: boolean }>`
  ${({ asGrid }) => (asGrid ? 'display: contents;' : '')}
`;

const TableBody = styled('tbody')<{ asGrid?: boolean }>`
  ${({ asGrid }) => (asGrid ? 'display: contents;' : '')}
`;

const MessageLink = styled('a')`
  text-decoration: underline;
  cursor: pointer;
`;

function Table<T>(props: TableProps<T>) {
  const {
    columns,
    customRowStyles,
    data,
    message,
    messageTag,
    fixedFirstRow,
    rowClickTag,
    onMessageClick,
    onRowClick,
    selectedIndexes,
    grid,
    autoLayout,
    children,
  } = props;

  const hideHeader = React.useMemo(() => columns.every((col) => !col.header), [columns]);

  const renderCell = (column: Column<T>, data: T, cellIndex: number, rowIndex: number) => {
    const {
      cell,
      noLeftPadding,
      noRightPadding,
      noPadding,
      width,
      verticalAlign,
      onCellClick,
      cursor,
      customCellStyles,
    } = column;

    const CellComponent = cell;

    const columnAlignment = verticalAlign || props.columnVerticalAlignment || undefined;

    const cellProps: CellProps = {
      rowIndex,
      noLeftPadding,
      noRightPadding,
      noPadding,
      cursor,
      verticalAlign: columnAlignment,
      customStyles:
        typeof customCellStyles === 'function'
          ? customCellStyles({ data, rowIndex })
          : customCellStyles,
    };

    if (hideHeader) {
      cellProps.width = width;
    }

    return (
      <TableCell
        {...cellProps}
        key={`cell-${rowIndex}-${cellIndex}`}
        onClick={onCellClick}
        asGrid={!!grid}
      >
        <CellComponent rowIndex={rowIndex} data={data} />
      </TableCell>
    );
  };

  const header = React.useMemo(
    () => (
      <TableHeader asGrid={!!grid}>
        <TableRow asGrid={!!grid}>
          {columns.map((column: Column<T>, index) => {
            const isComponent = typeof column.header !== 'string';
            const HeaderComponent = column.header as React.ComponentType;

            return (
              <TableCell
                isHeader
                key={`heading-${index}`}
                width={column.width}
                minWidth={column.minWidth}
                noLeftPadding={column.noLeftPadding}
                noRightPadding={column.noRightPadding}
                customStyles={
                  typeof column.customHeaderStyles === 'function'
                    ? column.customHeaderStyles()
                    : column.customHeaderStyles
                }
              >
                {isComponent ? <HeaderComponent /> : column.header}
              </TableCell>
            );
          })}
        </TableRow>
      </TableHeader>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns],
  );

  const rows = React.useMemo(
    () =>
      data.map((d, rowIndex) => (
        <TableRow
          isSelected={selectedIndexes ? selectedIndexes.includes(rowIndex) : false}
          rowIndex={rowIndex}
          key={`row-${rowIndex}`}
          onClick={onRowClick}
          dataPendo={rowClickTag || ''}
          customStyles={
            typeof customRowStyles === 'function'
              ? customRowStyles({ rowIndex, data: d })
              : customRowStyles
          }
          asGrid={!!grid}
        >
          {columns.map((column, cellIndex) => renderCell(column, d, cellIndex, rowIndex))}
        </TableRow>
      )),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, columns, selectedIndexes],
  );

  return (
    <Container className={props.className} grid={grid} autoLayout={autoLayout}>
      {!hideHeader && header}

      <TableBody asGrid={!!grid}>
        {/* Render message. Used for example for orders page "You have new orders" message */}
        {message && !grid && (
          <MessageRow onClick={onMessageClick} data-pendo={messageTag || ''}>
            <TableCell colSpan={columns.length}>
              <MessageLink>{message}</MessageLink>
            </TableCell>
          </MessageRow>
        )}

        {/* Render a fixed row. Used in batch orders table to get the select rates for all dropdown */}
        {fixedFirstRow && (
          <TableRow key={`custom-row`}>
            {(props.fixedFirstRow || []).map((column, cellIndex) =>
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              renderCell(column, null as any, cellIndex, 0),
            )}
          </TableRow>
        )}

        {rows}
      </TableBody>
      {children}
    </Container>
  );
}

export default Table;
